import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { parse, format } from 'date-fns';
import { useMemo, useRef } from 'react';
import { Form } from 'react-final-form';
import { Button, P } from 'shared';
import logoAFF from 'images/lenders/logo-alphaeon-access.svg';
import { AccessOfferEstimate } from '../../components';
import FieldsBanking from './FieldsBanking';
import FieldsPaymentFrequency from './FieldsPaymentFrequency';
import FieldsWisconsin from './FieldsWisconsin';
import TermsAgreementShell from './TermsAgreementShell';
import { validateAFF } from './utils/validate';
const PreQualAccessForm = ({ submitFunc, isSubmitting, onSubmitFail, submitButtonText, lenderName, onSubmitSuccess, locationId, personalInfo, prequalification, }) => {
    const paymentFrequencyRef = useRef(null);
    const bankingDetailsRef = useRef(null);
    const wisconsinDetailsRef = useRef(null);
    const lenderProps = useMemo(() => {
        let tmpLogo = '';
        let tmpDesc = '';
        let tmpNick = '';
        const noFieldsToShow = false;
        let formValidate = (values) => {
            if (Object.keys(values).length) {
                const errors = {};
                return errors;
            }
            else {
                return undefined;
            }
        };
        switch (lenderName) {
            case 'AFF':
                tmpLogo = logoAFF;
                tmpDesc = 'American First Finance';
                tmpNick = 'AFF';
                formValidate = validateAFF;
                break;
            default:
                break;
        }
        return {
            logo: tmpLogo,
            desc: tmpDesc,
            nick: tmpNick,
            formValidate: formValidate,
            noFieldsToShow: noFieldsToShow,
        };
    }, [lenderName]);
    // Func submit form
    const formSubmit = async (values) => {
        const isFormValid = lenderProps.formValidate(values);
        if (isFormValid === undefined) {
            const res = await submitFunc({
                lender_required_params: {
                    ...values,
                    tcpa_consent: values.termsAgreedTo1 && values.termsAgreedTo1.length > 0
                        ? true
                        : false,
                    income_pay_next: values.income_pay_next
                        ? format(parse(values.income_pay_next, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd')
                        : undefined,
                    income_pay_last: values.income_pay_last
                        ? format(parse(values.income_pay_last, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd')
                        : undefined,
                },
            });
            if (res) {
                onSubmitSuccess(res);
            }
            else {
                onSubmitFail('Sorry, there was an error submitting the form');
            }
        }
    };
    const financedAmount = useMemo(() => prequalification.offers.filter((offer) => {
        return offer.serviceProviderName === 'AFF';
    })[0]?.amount, [prequalification]);
    const isLTO = prequalification.offers.some((offer) => offer.lenderProductType === 'LTO');
    const incomeFrequencyValuesMap = {
        W: 'weekly',
        B: 'bi_weekly',
        M: 'monthly',
        T: 'semi_monthly',
    };
    const errorToRefMap = {
        income_frequency: paymentFrequencyRef,
        stateCode: wisconsinDetailsRef,
        income_pay_next: paymentFrequencyRef,
        income_pay_last: paymentFrequencyRef,
        account_aba_number: bankingDetailsRef,
        account_number: bankingDetailsRef,
        bank_name: bankingDetailsRef,
        married: wisconsinDetailsRef,
        spouse_live_with: wisconsinDetailsRef,
        address_spouse_state: wisconsinDetailsRef,
        spouse_name: wisconsinDetailsRef,
        address_spouse_line1: wisconsinDetailsRef,
        address_spouse_line2: wisconsinDetailsRef,
        address_spouse_city: wisconsinDetailsRef,
        address_spouse_zip: wisconsinDetailsRef,
    };
    const handleScrollToError = (errorKey) => {
        if (!errorKey.includes('termsAgreedTo')) {
            errorToRefMap[errorKey]?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };
    return (_jsx("div", { children: _jsx("div", { className: "pre-qualification-installment", children: _jsx("div", { className: "form-shell", children: _jsx(Form, { initialValues: {}, onSubmit: formSubmit, validate: lenderProps.formValidate, render: ({ handleSubmit, pristine, submitting, values, hasValidationErrors, errors, form, }) => {
                        // Button Disabled Bool
                        const btnDisabled = (pristine && !lenderProps.noFieldsToShow) ||
                            submitting ||
                            hasValidationErrors;
                        const buttonText = submitting ? '...Submitting' : submitButtonText;
                        return (_jsxs("form", { onSubmit: handleSubmit, children: [lenderName === 'AFF' ? (_jsxs(_Fragment, { children: [_jsx(FieldsPaymentFrequency, { values: values, ref: paymentFrequencyRef }), _jsx(AccessOfferEstimate, { isLTO: isLTO, applicationId: prequalification.applicationId, financedAmount: financedAmount, paymentFrequency: incomeFrequencyValuesMap[values.income_frequency] ?? '' }), _jsx(FieldsBanking, { ref: bankingDetailsRef, routingNumber: values.account_aba_number ?? '', form: form }), personalInfo.state === 'WI' ? (_jsx(FieldsWisconsin, { personalInfo: personalInfo, ref: wisconsinDetailsRef })) : null, _jsx(TermsAgreementShell, { locationId: locationId, lenderName: lenderName })] })) : null, _jsxs("div", { className: "send-receipt-buttons pad-40", children: [_jsx(Button, { type: "submit", "data-testid": "access-form-submit-button", className: `send-receipt-modal-btn button-primary button-warn ${btnDisabled ? 'is-disabled' : 'is-enabled'} ${submitting || isSubmitting ? 'in-progress' : ''}`, invalid: btnDisabled && !(pristine || isSubmitting || submitting), disabledToolTip: "Please fill out all required fields and correct any validation errors.", children: buttonText }), _jsx("br", {}), _jsx("br", {}), !pristine && hasValidationErrors && errors
                                            ? Object.keys(errors).map((errorKey, index) => (_jsxs("span", { className: "error-message clickable-el", tabIndex: 0, role: "button", onClick: () => handleScrollToError(errorKey), onKeyDown: (e) => {
                                                    if (e.key === 'Enter') {
                                                        handleScrollToError(errorKey);
                                                    }
                                                }, children: [errors[errorKey], Object.keys(errors).length !== index + 1
                                                        ? ','
                                                        : '', "\u00A0"] }, errorKey)))
                                            : null, _jsx("br", {})] }), _jsxs(P, { children: [_jsx("sup", { children: "\u2020" }), " \u00A0 While this amount is requested, please note that some providers do not take the Intended Transaction Amount into account for their decision."] })] }));
                    } }) }) }, `prequal-form`) }));
};
export { PreQualAccessForm as default };
